import { NetworkType } from "apollo/graphql.generated";

export const CLIENT_VERSION = 1;

const getRequiredEnvVar = (name: string) => {
  const value = import.meta.env[name];
  if (!value) {
    console.error(`env var ${name} is not set`);
  }
  return value as string;
};

export const envConfig = {
  graphql: {
    httpUrl: getRequiredEnvVar("REACT_APP_WEB_GRAPHQL_URL"),
  },
  isDevelopment: import.meta.env.REACT_APP_ENV !== "production",
};

export const mintConfig = {
  mintFunction: getRequiredEnvVar("REACT_APP_MINT_FUNCTION"),
  mintPort: "3003",
};

const { protocol, host } = window.location;

export const MINT_BASE_URL = getRequiredEnvVar("REACT_APP_MINT_BASE_URL");
export const MINT_CDN_URL =
  import.meta.env.REACT_APP_MINT_CDN_URL ?? MINT_BASE_URL;

const rootDomain = (domain: string) =>
  domain.split(".").reverse().splice(0, 2).reverse().join(".");

const MINT_DOMAIN = new URL(MINT_BASE_URL).host;

export const PORTFOLIO_BASE_URL = host.includes("localhost")
  ? MINT_BASE_URL
  : `${protocol}//${rootDomain(MINT_DOMAIN)}`;

export const GATES_BASE_URL = getRequiredEnvVar("REACT_APP_GATES_BASE_URL");

/* URLS */
export const TOOLS_URL = Object.freeze({
  home: "/",
  debugRoot: "/debug",
  debug: {
    draftDeploy: "/debug/draft/:draftId/deploy",
  },
  pages: {
    list: "/pages",
    create: "/pages/create",
    edit: "/pages/:pageId/edit",
  },
  gates: {
    list: "/gates",
    create: "/gates/create",
    edit: "/gates/:gateId/edit",
  },
  tokens: {
    manageImportedToken: "/tokens/imported/:tokenId/manage",
  },
  collections: {
    create: "/collections/create",
    createType: "/collections/create/type/:collectionType",
    resumeCreate: "/collections/create/:collectionId",
    manage: "/collections/:collectionId/manage",
  },
  portfolio: {
    home: "/portfolio",
    list: "/portfolios",
    editor: "/portfolios/:portfolioId",
    twitter: {
      callback: "/portfolio/twitter/callback",
    },
  },
  account: {
    settings: {
      email: "/account/settings/email",
      emailVerify: "/account/settings/email/verify",
    },
  },
  contract: {
    list: "/contracts",
    detail: "/contract/detail/:contractId",
  },
});

export const TOOLS_CDN_URL =
  import.meta.env["REACT_APP_TOOLS_CDN_URL"] ??
  import.meta.env["REACT_APP_TOOLS_BASE_URL"];
if (!TOOLS_CDN_URL) {
  console.warn("TOOLS_CDN_URL is not defined");
}

export const MINT_URL = Object.freeze({
  tokens: {
    mint: `${MINT_BASE_URL}/:collectionId`,
    detail: `${MINT_BASE_URL}/:collectionId/t/:tokenId`,
    reveal: `${MINT_BASE_URL}/:collectionId/reveal`,
    marketplace: `${MINT_BASE_URL}/:collectionId/marketplace`,
  },
});

export const GATES_URL = Object.freeze({
  page: {
    view: `${GATES_BASE_URL}/page/:pageSlug`,
  },
});

export const EXTERNAL_URL = Object.freeze({
  SUPPORT: "https://support.highlight.xyz",
  SUPPORT_ARTWORK_GLOSSARY:
    "https://support.highlight.xyz/knowledge-base/get-started/highlight-glossary",
  OPENSEA: "https://opensea.io/assets",
  OPENSEA_TESTNET: "https://testnets.opensea.io/assets",
  POLYGONSCAN: "https://polygonscan.com",
  MUMBAISCAN: "https://mumbai.polygonscan.com/",
  ETHERSCAN: "https://etherscan.io",
  GOERLISCAN: "https://goerli.etherscan.io/",
  SEPOLIASCAN: "https://sepolia.etherscan.io/",
  ARBISCAN: "https://arbiscan.io/",
  ARBIGOERLISCAN: "https://goerli.arbiscan.io/",
  OPTIMISMSCAN: "https://optimistic.etherscan.io/",
  OPTIMISTICGOERLISCAN: "https://goerli-optimism.etherscan.io/",
  BASEGOERLISCAN: "https://goerli.basescan.org/",
  BASESEPOLIASCAN: "https://sepolia.basescan.org/",
});

export const SUPPORT_URL = Object.freeze({
  GATES_LEARN_MORE:
    "https://support.highlight.xyz/knowledge-base/get-started/token-gate-anything/what-is-a-gate",
  SERIES_LEARN_MORE:
    "https://support.highlight.xyz/knowledge-base/get-started/create-a-collection/drop-a-series-style-collection",
  JSON_CSV_CONVERTER: "https://www.convertcsv.com/csv-to-json.htm",
  GEN_ART_REPO: "https://github.com/highlightxyz/generative-art",
  GEN_ART_EXAMPLE:
    "https://github.com/highlightxyz/generative-art/raw/main/examples/basic-p5-example/BASIC-P5-EXAMPLE-UPLOAD-ME.zip",
  ENGLISH_AUCTIONS:
    "https://support.highlight.xyz/knowledge-base/get-started/list-your-collection-for-sale/auctions/english-auction",
  RANKED_AUCTIONS:
    "https://support.highlight.xyz/knowledge-base/get-started/list-your-collection-for-sale/auctions/ranked-auction",
  DUTCH_AUCTIONS:
    "https://support.highlight.xyz/knowledge-base/get-started/list-your-collection-for-sale/auctions/dutch-auction",
});

/* STORAGE KEYS */
export const STORAGE_KEYS = Object.freeze({
  auth: "highlight.auth",
  analyticsDevice: "highlight.analytics.device",
});

export const JWT_EXPIRATION_DAYS = 10; // in days
export const IN_DEVELOPMENT = import.meta.env.REACT_APP_ENV !== "production";
export const GOOGLE_ANALYTICS_ID =
  import.meta.env.REACT_APP_GOOGLE_ANALYTICS_ID || null;
export const ALCHEMY_API_KEY_ENS = import.meta.env
  .REACT_APP_ALCHEMY_API_KEY_ENS;

export enum ApolloClientErrors {
  CLIENT_VERSION_DONT_MATCH = "ClientVersionDontMatch",
}

export const DEFAULT_ERROR_MESSAGE =
  "A server error occurred, please try again. If this error persists, contact us at support@highlight.xyz with details.";

export const BlockchainMainnets = [
  NetworkType.ETHEREUM,
  NetworkType.POLYGON,
  NetworkType.ARBITRUM,
  NetworkType.OPTIMISM,
  NetworkType.BASE,
  NetworkType.ZORA,
  NetworkType.FORMA,
  NetworkType.MANTLE,
  NetworkType.SCROLL,
];

export const BlockchainTestnets = [
  NetworkType.GOERLI,
  NetworkType.MUMBAI,
  NetworkType.ARBITRUM_GOERLI,
  NetworkType.OPTIMISM_GOERLI,
  NetworkType.BASE_GOERLI,
  NetworkType.ZORA_GOERLI,
  NetworkType.SEPOLIA,
  NetworkType.BASE_SEPOLIA,
];

export const CrossmintNetworks = [
  NetworkType.SEPOLIA,
  NetworkType.GOERLI,
  NetworkType.POLYGON,
  NetworkType.ETHEREUM,
  NetworkType.ARBITRUM,
  NetworkType.OPTIMISM,
  NetworkType.BASE,
  NetworkType.ZORA,
];

export const isTestnetNetwork = (network: NetworkType) =>
  BlockchainTestnets.includes(network);

export const MAILCHIMP_FORM = Object.freeze({
  url: "https://xyz.us5.list-manage.com/subscribe/post?u=64e00b1bdf3cfd22c1ddcd0ae&amp;id=7372613280&amp;f_id=00ce82e9f0",
  validation: "b_64e00b1bdf3cfd22c1ddcd0ae_7372613280",
});
